import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import { fetchGPBasedOnOrders } from "../../store/slices/gpHierarchySlice";
import { GPHierarchyNode, GPMetrics } from "../../api/gpHierarchy.api";
import { format, startOfMonth, subMonths } from "date-fns";
import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Users,
  X,
} from "lucide-react";

// --- Utility Functions ---

const formatGp = (v: number) =>
  `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const getGpColorClass = (
  value: number,
  type: "own" | "team" | "total" = "total",
) => {
  if (value === 0) return "text-gray-500 font-normal";

  const thresholds =
    type === "total" ? [10000, 50000, 100000] : [5000, 20000, 50000];
  const colors = {
    own: ["text-blue-600", "text-blue-700", "text-blue-800", "text-blue-900"],
    team: [
      "text-indigo-600",
      "text-indigo-700",
      "text-indigo-800",
      "text-indigo-900",
    ],
    total: [
      "text-green-600",
      "text-green-700",
      "text-green-800",
      "text-green-900",
    ],
  };

  const colorSet = colors[type];
  if (value < thresholds[0]) return colorSet[0];
  if (value < thresholds[1]) return colorSet[1];
  if (value < thresholds[2]) return colorSet[2];
  return colorSet[3];
};

type Node = GPHierarchyNode;
type HierarchyPerson = Pick<Node, "_id" | "username" | "role">;
type PartnerDealerEntry = {
  node: Node;
  hierarchyPath: HierarchyPerson[];
};

const gridClasses =
  "min-w-[1180px] grid-cols-[minmax(320px,2fr)_140px_115px_115px_125px_100px_minmax(300px,1.5fr)] gap-x-3";

const getRoleConfig = (role: string) => {
  const normalizedRole = role.toLowerCase();
  const config = {
    admin: {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
      icon: "👑",
      level: 1,
    },
    sh: {
      bg: "bg-indigo-50",
      text: "text-indigo-700",
      border: "border-indigo-200",
      icon: "🎯",
      level: 2,
    },
    bdm: {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
      icon: "📊",
      level: 3,
    },
    bde: {
      bg: "bg-teal-50",
      text: "text-teal-700",
      border: "border-teal-200",
      icon: "💼",
      level: 4,
    },
    se: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      icon: "🧭",
      level: 5,
    },
    factory: {
      bg: "bg-orange-50",
      text: "text-orange-700",
      border: "border-orange-200",
      icon: "🏭",
      level: 5,
    },
    account: {
      bg: "bg-violet-50",
      text: "text-violet-700",
      border: "border-violet-200",
      icon: "🧾",
      level: 5,
    },
    telecaller: {
      bg: "bg-cyan-50",
      text: "text-cyan-700",
      border: "border-cyan-200",
      icon: "☎️",
      level: 5,
    },
    distributor: {
      bg: "bg-lime-50",
      text: "text-lime-700",
      border: "border-lime-200",
      icon: "📦",
      level: 5,
    },
    "data entry": {
      bg: "bg-pink-50",
      text: "text-pink-700",
      border: "border-pink-200",
      icon: "⌨️",
      level: 5,
    },
    digitalmarketing: {
      bg: "bg-fuchsia-50",
      text: "text-fuchsia-700",
      border: "border-fuchsia-200",
      icon: "📣",
      level: 5,
    },
    floor: {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      border: "border-yellow-200",
      icon: "🧰",
      level: 5,
    },
    store: {
      bg: "bg-sky-50",
      text: "text-sky-700",
      border: "border-sky-200",
      icon: "🏬",
      level: 5,
    },
    service: {
      bg: "bg-rose-50",
      text: "text-rose-700",
      border: "border-rose-200",
      icon: "🛠️",
      level: 5,
    },
    sp: {
      bg: "bg-sky-50",
      text: "text-sky-800",
      border: "border-sky-300",
      icon: "📍",
      level: 5,
    },
    hr: {
      bg: "bg-purple-50",
      text: "text-purple-700",
      border: "border-purple-200",
      icon: "👥",
      level: 5,
    },
    le: {
      bg: "bg-orange-50",
      text: "text-orange-800",
      border: "border-orange-300",
      icon: "📋",
      level: 5,
    },
    cse: {
      bg: "bg-teal-50",
      text: "text-teal-800",
      border: "border-teal-300",
      icon: "🎧",
      level: 5,
    },
    "dispatch-lv1": {
      bg: "bg-slate-100",
      text: "text-slate-700",
      border: "border-slate-300",
      icon: "🚚",
      level: 5,
    },
    "dispatch-lv2": {
      bg: "bg-stone-100",
      text: "text-stone-700",
      border: "border-stone-300",
      icon: "📦",
      level: 5,
    },
  };

  return (
    config[normalizedRole as keyof typeof config] || {
      bg: "bg-slate-50",
      text: "text-slate-600",
      border: "border-slate-200",
      icon: "👤",
      level: 5,
    }
  );
};
// --- Child Components ---

const SummaryCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  detail?: string;
  variant?: "gp" | "sales" | "support";
}> = ({ title, value, subtitle, detail, variant = "support" }) => {
  const isGp = variant === "gp";
  const isSales = variant === "sales";

  return (
    <div
      className={`relative flex min-h-[112px] min-w-0 flex-col justify-between overflow-hidden rounded-xl border p-4 shadow-sm sm:min-h-[132px] sm:p-5 ${
        isGp || isSales ? "col-span-2" : ""
      } ${
        isGp
          ? "border-emerald-900 bg-gradient-to-br from-emerald-950 to-emerald-800 text-white"
          : isSales
            ? "border-teal-200 bg-gradient-to-br from-teal-50 to-white text-slate-900"
            : "border-slate-200 bg-white text-slate-900"
      }`}
    >
      {isGp && (
        <div className="pointer-events-none absolute -right-5 -top-7 h-24 w-24 rounded-full border-[12px] border-white/10" />
      )}
      <div className="relative">
        <div
          className={`mb-2 text-[11px] font-bold uppercase tracking-[0.12em] ${
            isGp
              ? "text-emerald-100"
              : isSales
                ? "text-teal-800"
                : "text-slate-500"
          }`}
        >
          {title}
        </div>
        <div
          className={`truncate font-bold tabular-nums ${
            isGp
              ? "text-2xl sm:text-3xl"
              : isSales
                ? "text-xl sm:text-2xl"
                : "text-xl"
          }`}
        >
          {value}
        </div>
      </div>
      {(subtitle || detail) && (
        <div className="relative mt-3">
          {subtitle && (
            <div
              className={`text-xs ${
                isGp ? "text-emerald-100" : "text-slate-500"
              }`}
            >
              {subtitle}
            </div>
          )}
          {detail && (
            <div
              className={`mt-1 text-xs font-semibold ${
                isGp ? "text-white" : "text-teal-800"
              }`}
            >
              {detail}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PartnerDealerModal: React.FC<{
  channelPartners: PartnerDealerEntry[];
  commissionDealers: PartnerDealerEntry[];
  filterPeople: HierarchyPerson[];
  selectedUserIds: string[] | null;
  selectedCount: number;
  totalUsers: number;
  onSelectionChange: (nodeId: string, selected: boolean) => void;
  onClose: () => void;
}> = ({
  channelPartners,
  commissionDealers,
  filterPeople,
  selectedUserIds,
  selectedCount,
  totalUsers,
  onSelectionChange,
  onClose,
}) => {
  const [selectedPersonId, setSelectedPersonId] = useState("all");

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const visibleEntries = (entries: PartnerDealerEntry[]) =>
    selectedPersonId === "all"
      ? entries
      : entries.filter((entry) =>
          entry.hierarchyPath.some((person) => person._id === selectedPersonId),
        );

  const groups = [
    { title: "Channel Partners", entries: visibleEntries(channelPartners) },
    { title: "Commission Dealers", entries: visibleEntries(commissionDealers) },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/55 p-0 backdrop-blur-[2px] sm:items-center sm:p-5"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-dealer-dialog-title"
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-xl border border-slate-200 bg-white shadow-2xl sm:rounded-xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-800">
              Separate accounts
            </p>
            <h2
              id="partner-dealer-dialog-title"
              className="mt-1 text-lg font-semibold text-slate-950"
            >
              Channel Partners &amp; Commission Dealers
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Select or clear these accounts to update hierarchy totals.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close partners and dealers"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-700"
          >
            <X size={18} />
          </button>
        </header>

        <div className="overflow-y-auto p-4 sm:p-6">
          <label className="mb-5 flex flex-col gap-1.5 text-xs font-semibold text-slate-700 sm:max-w-sm">
            Show accounts under
            <select
              value={selectedPersonId}
              onChange={(event) => setSelectedPersonId(event.target.value)}
              className="min-h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
            >
              <option value="all">Everyone</option>
              {filterPeople.map((person) => (
                <option key={person._id} value={person._id}>
                  {person.username} ({person.role.toUpperCase()})
                </option>
              ))}
            </select>
          </label>
          {groups.map(({ title, entries }) => (
            <section key={title} className="mb-6 last:mb-0">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  {title}
                </h3>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-700">
                  {entries.length}
                </span>
              </div>
              {entries.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                  No {title.toLowerCase()} found under this person.
                </p>
              ) : (
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {entries.map(({ node, hierarchyPath }) => (
                    <label
                      key={node._id}
                      className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-3 p-3 transition-colors hover:bg-emerald-50/50 sm:grid-cols-[auto_minmax(0,1fr)_repeat(4,minmax(78px,auto))] sm:items-center sm:gap-4"
                    >
                      <input
                        type="checkbox"
                        checked={
                          selectedUserIds === null ||
                          selectedUserIds.includes(node._id)
                        }
                        onChange={(event) =>
                          onSelectionChange(node._id, event.target.checked)
                        }
                        aria-label={`Select ${node.username}`}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600 sm:mt-0"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-900">
                          {node.username}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {node.orderCount} orders
                        </span>
                        <span className="mt-1 block truncate text-xs text-slate-600">
                          Under:{" "}
                          {hierarchyPath.length
                            ? hierarchyPath
                                .map((person) => person.username)
                                .join(" > ")
                            : "Top-level"}
                        </span>
                      </span>
                      <span className="col-start-2 text-xs sm:col-start-auto">
                        <span className="block text-[10px] font-semibold uppercase text-slate-500">
                          Sales
                        </span>
                        <span className="font-semibold text-slate-800">
                          {formatGp(node.own.orderValue)}
                        </span>
                      </span>
                      <span className="text-xs">
                        <span className="block text-[10px] font-semibold uppercase text-slate-500">
                          Own GP
                        </span>
                        <span className="font-semibold text-blue-800">
                          {formatGp(node.own.gp)}
                        </span>
                      </span>
                      <span className="text-xs">
                        <span className="block text-[10px] font-semibold uppercase text-slate-500">
                          Team GP
                        </span>
                        <span className="font-semibold text-indigo-800">
                          {formatGp(node.team.gp)}
                        </span>
                      </span>
                      <span className="text-xs">
                        <span className="block text-[10px] font-semibold uppercase text-slate-500">
                          Total GP
                        </span>
                        <span className="font-bold text-emerald-900">
                          {formatGp(node.gp)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-6">
          <span className="text-xs text-slate-600">
            {selectedCount} of {totalUsers} users selected
          </span>
          <button
            type="button"
            onClick={onClose}
            className="min-h-10 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-2"
          >
            Done
          </button>
        </footer>
      </section>
    </div>
  );
};

const OrderNumbersList: React.FC<{ orderNumbers: string[] }> = ({
  orderNumbers,
}) => {
  const [showAll, setShowAll] = useState(false);
  const visibleOrders = showAll ? orderNumbers : orderNumbers.slice(0, 3);

  if (orderNumbers.length === 0) {
    return <span className="text-gray-400 italic text-sm">No orders</span>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {visibleOrders.map((orderNum) => (
          <span
            key={orderNum}
            className="bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-lg text-xs font-mono text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-help shadow-xs"
            title={`Order: ${orderNum}`}
          >
            {orderNum}
          </span>
        ))}
      </div>
      {orderNumbers.length > 3 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-xs text-blue-700 hover:text-blue-900 font-medium transition-colors"
        >
          {showAll ? `Show less` : `+${orderNumbers.length - 3} more`}
        </button>
      )}
    </div>
  );
};

const NodeRow: React.FC<{
  node: Node;
  level: number;
  isExpanded: boolean;
  onToggle: (nodeId: string) => void;
  isSelected: boolean;
  onSelectionChange: (nodeId: string, selected: boolean) => void;
  selectedUserIds: string[] | null;
}> = ({
  node,
  level,
  isExpanded,
  onToggle,
  isSelected,
  onSelectionChange,
  selectedUserIds,
}) => {
  const roleConfig = getRoleConfig(node.role);
  const allChildren = node.children || [];

  const hasChildren = allChildren.length > 0;
  const indent = level * 28 + 16;

  const totalGpBgClass = `bg-gray-50 rounded-lg border border-gray-200`;

  return (
    <>
      <div
        className={`hidden ${gridClasses} items-center border-b border-gray-100 last:border-b-0 hover:bg-emerald-50/40 transition-colors group md:grid`}
        style={{
          backgroundColor: level === 0 ? "#f7faf9" : undefined,
        }}
      >
        <div
          className="py-3 px-4 min-w-0 flex items-center gap-2"
          style={{ paddingLeft: `${indent}px` }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(event) =>
              onSelectionChange(node._id, event.target.checked)
            }
            aria-label={`Select ${node.username}`}
            className="h-4 w-4 shrink-0 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
          />
          {hasChildren ? (
            <button
              onClick={() => onToggle(node._id)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-600"
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? "Collapse" : "Expand"} ${node.username}`}
            >
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          ) : (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center">
              <div className="h-1 w-1 rounded-full bg-gray-300" />
            </div>
          )}

          <div className="ml-1 flex min-w-0 flex-1 items-center gap-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${roleConfig.bg} border ${roleConfig.border} text-xs font-bold ${roleConfig.text}`}
            >
              {node.username
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold text-gray-900">
                  {node.username}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 ${roleConfig.bg} ${roleConfig.text} border ${roleConfig.border} text-[10px] rounded font-semibold tracking-wide`}
                >
                  {node.role.toUpperCase()}
                </span>
                {hasChildren && (
                  <span className="text-xs text-gray-500">
                    {allChildren.length} direct report
                    {allChildren.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="py-3 px-2 text-right text-sm font-medium">
          <div className="flex flex-col items-end">
            <span className="font-semibold text-gray-900">
              {formatGp(node.own.orderValue)}
            </span>
            {node.own.orderValue > 0 && (
              <span className="text-xs text-gray-500">
                {((node.own.gp / node.own.orderValue) * 100).toFixed(1)}% GP
              </span>
            )}
          </div>
        </div>
        <div
          className={`py-3 px-2 text-right text-sm font-semibold ${getGpColorClass(
            node.own.gp,
            "own",
          )}`}
        >
          {formatGp(node.own.gp)}
        </div>
        <div
          className={`py-3 px-2 text-right text-sm font-semibold ${getGpColorClass(
            node.team.gp,
            "team",
          )}`}
        >
          {formatGp(node.team.gp)}
        </div>
        <div
          className={`py-3 px-3 text-right text-sm font-bold ${getGpColorClass(
            node.gp,
            "total",
          )} ${totalGpBgClass}`}
        >
          {formatGp(node.gp)}
        </div>

        <div className="py-3 px-2 flex justify-center">
          <div className="relative">
            <span
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${
                node.orderCount === 0
                  ? "bg-gray-100 text-gray-500"
                  : "bg-blue-100 text-blue-700 shadow-xs border border-blue-200"
              }`}
            >
              {node.orderCount}
              {node.orderCount > 0 && (
                <span className="ml-1 text-xs">orders</span>
              )}
            </span>
          </div>
        </div>

        <div className="py-3 px-4 min-w-0">
          <OrderNumbersList orderNumbers={node.orderNumbers} />
        </div>
      </div>

      <div
        className={`border-b border-gray-100 px-3 py-3 md:hidden ${
          level === 0 ? "bg-[#f7faf9]" : "bg-white"
        }`}
      >
        <div
          className="flex min-w-0 items-center gap-2"
          style={{ paddingLeft: `${Math.min(level, 4) * 12}px` }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(event) =>
              onSelectionChange(node._id, event.target.checked)
            }
            aria-label={`Select ${node.username}`}
            className="h-4 w-4 shrink-0 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
          />
          {hasChildren ? (
            <button
              onClick={() => onToggle(node._id)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600"
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? "Collapse" : "Expand"} ${node.username}`}
            >
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          ) : (
            <span className="w-7 shrink-0" />
          )}
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${roleConfig.bg} border ${roleConfig.border} text-xs font-bold ${roleConfig.text}`}
          >
            {node.username
              .split(" ")
              .map((part) => part[0])
              .join("")
              .toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900">
              {node.username}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span
                className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${roleConfig.bg} ${roleConfig.text} ${roleConfig.border}`}
              >
                {node.role.toUpperCase()}
              </span>
              {hasChildren && (
                <span className="text-[11px] text-gray-500">
                  {allChildren.length} report
                  {allChildren.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-100 pt-2 pl-9">
          <div>
            <div className="text-[10px] font-semibold uppercase text-gray-500">
              Sales
            </div>
            <div className="text-xs font-medium text-gray-900">
              {formatGp(node.own.orderValue)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-gray-500">
              Own GP
            </div>
            <div className="text-xs font-medium text-blue-800">
              {formatGp(node.own.gp)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-gray-500">
              Team GP
            </div>
            <div className="text-xs font-medium text-indigo-800">
              {formatGp(node.team.gp)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-gray-500">
              Total GP · {node.orderCount} orders
            </div>
            <div className="text-xs font-bold text-emerald-900">
              {formatGp(node.gp)}
            </div>
          </div>
          <div className="col-span-2 min-w-0">
            <div className="mb-1 text-[10px] font-semibold uppercase text-gray-500">
              Order numbers
            </div>
            <OrderNumbersList orderNumbers={node.orderNumbers} />
          </div>
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div className="animate-fadeIn">
          {allChildren.map((child) => (
            <ConnectedNodeRow
              key={child._id}
              node={child}
              level={level + 1}
              selectedUserIds={selectedUserIds}
              onSelectionChange={onSelectionChange}
            />
          ))}
        </div>
      )}
    </>
  );
};

const findLastNodeId = (nodes: Node[]): string | null => {
  if (!nodes || nodes.length === 0) return null;

  const lastNode = nodes[nodes.length - 1];

  if (lastNode.children.length > 0) {
    const lastChildId = findLastNodeId(lastNode.children);
    if (lastChildId) {
      return lastChildId;
    }
  }

  return lastNode._id;
};

const ConnectedNodeRow: React.FC<{
  node: Node;
  level: number;
  selectedUserIds: string[] | null;
  onSelectionChange: (nodeId: string, selected: boolean) => void;
}> = ({ node, level, selectedUserIds, onSelectionChange }) => {
  const { hierarchy } = useSelector((state: RootState) => state.gpHierarchy);

  const [isExpanded, setIsExpanded] = useState(level < 2);

  const lastNodeId = useMemo(() => {
    return findLastNodeId(hierarchy);
  }, [hierarchy]);

  useEffect(() => {
    const isNodeOnPath = (currentNode: Node, targetId: string): boolean => {
      if (currentNode._id === targetId) {
        return true;
      }

      return currentNode.children.some((child) =>
        isNodeOnPath(child, targetId),
      );
    };

    if (lastNodeId) {
      const shouldExpand = isNodeOnPath(node, lastNodeId);
      if (shouldExpand) {
        setIsExpanded(true);
      }
    }
  }, [node, lastNodeId]);

  const onToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <NodeRow
      node={node}
      level={level}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isSelected={
        selectedUserIds === null || selectedUserIds.includes(node._id)
      }
      selectedUserIds={selectedUserIds}
      onSelectionChange={onSelectionChange}
    />
  );
};

const DatePresets: React.FC<{
  onPresetSelect: (from: string, to: string) => void;
}> = ({ onPresetSelect }) => {
  const presets = [
    {
      label: "Today",
      getDates: () => {
        const d = new Date();
        return [d, d];
      },
    },
    {
      label: "This Week",
      getDates: () => {
        const d = new Date();
        const start = new Date(d.setDate(d.getDate() - d.getDay()));
        return [start, new Date()];
      },
    },
    {
      label: "This Month",
      getDates: () => [startOfMonth(new Date()), new Date()],
    },
    {
      label: "Last Month",
      getDates: () => {
        const d = subMonths(new Date(), 1);
        return [startOfMonth(d), startOfMonth(new Date())];
      },
    },
    {
      label: "Last 3 Months",
      getDates: () => {
        const d = new Date();
        return [subMonths(d, 3), d];
      },
    },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {presets.map((preset) => (
        <button
          key={preset.label}
          onClick={() => {
            const [from, to] = preset.getDates();
            onPresetSelect(
              format(from, "yyyy-MM-dd"),
              format(to, "yyyy-MM-dd"),
            );
          }}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:border-emerald-700 hover:bg-emerald-50 hover:text-emerald-900"
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
};

const getAllNodes = (nodes: Node[]): Node[] => {
  return nodes.flatMap((node) => {
    return [node, ...getAllNodes(node.children || [])];
  });
};

const isPartnerOrDealer = (node: Node): boolean => {
  const role = node.role.trim().toLowerCase();
  return role === "channel partner" || role === "commission dealer";
};

const partitionPartnerAndDealerNodes = (nodes: Node[]) => {
  const specialNodes: PartnerDealerEntry[] = [];

  const collectVisibleNodes = (
    currentNodes: Node[],
    hierarchyPath: HierarchyPerson[] = [],
  ): Node[] =>
    currentNodes.flatMap((node) => {
      const nodePerson = {
        _id: node._id,
        username: node.username,
        role: node.role,
      };
      const children = collectVisibleNodes(node.children || [], [
        ...hierarchyPath,
        nodePerson,
      ]);
      if (isPartnerOrDealer(node)) {
        specialNodes.push({ node, hierarchyPath });
        return children;
      }
      return [{ ...node, children }];
    });

  return {
    visibleHierarchy: collectVisibleNodes(nodes),
    specialNodes,
  };
};

const emptyMetrics = (metrics: GPMetrics): GPMetrics => ({
  ...metrics,
  orderCount: 0,
  orderNumbers: [],
  orderValue: 0,
  allocationCost: 0,
  salesRepCommission: 0,
  channelPartnerRepayment: 0,
  channelPartnerCharges: 0,
  gp: 0,
});

const mergeHierarchyMetrics = (structure: Node[], response: Node[]): Node[] => {
  const responseById = new Map(
    getAllNodes(response).map((node) => [node._id, node]),
  );

  const mergeNode = (node: Node): Node => {
    const updated = responseById.get(node._id);
    if (!updated) {
      return {
        ...node,
        own: emptyMetrics(node.own),
        team: emptyMetrics(node.team),
        orderCount: 0,
        orderNumbers: [],
        orderValue: 0,
        allocationCost: 0,
        salesRepCommission: 0,
        channelPartnerRepayment: 0,
        channelPartnerCharges: 0,
        gp: 0,
        children: node.children.map(mergeNode),
      };
    }

    return {
      ...node,
      ...updated,
      children: node.children.map(mergeNode),
    };
  };

  return structure.map(mergeNode);
};

const HierarchyLoadingSkeleton: React.FC = () => (
  <div role="status" aria-label="Loading GP hierarchy">
    {Array.from({ length: 6 }, (_, index) => (
      <React.Fragment key={index}>
        <div
          className={`hidden ${gridClasses} animate-pulse items-center border-b border-gray-100 md:grid`}
        >
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="h-4 w-4 rounded bg-gray-200" />
            <div className="h-9 w-9 rounded-lg bg-gray-200" />
            <div className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200" />
              <div className="h-2 w-16 rounded bg-gray-100" />
            </div>
          </div>
          {Array.from({ length: 6 }, (_, cellIndex) => (
            <div key={cellIndex} className="px-3 py-4">
              <div className="ml-auto h-3 w-14 rounded bg-gray-200" />
            </div>
          ))}
        </div>
        <div className="animate-pulse border-b border-gray-100 px-3 py-4 md:hidden">
          <div className="flex items-center gap-3">
            <div className="h-4 w-4 rounded bg-gray-200" />
            <div className="h-9 w-9 rounded-lg bg-gray-200" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-32 rounded bg-gray-200" />
              <div className="h-2 w-20 rounded bg-gray-100" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 pl-12">
            {Array.from({ length: 4 }, (_, metricIndex) => (
              <div key={metricIndex} className="space-y-2">
                <div className="h-2 w-12 rounded bg-gray-100" />
                <div className="h-3 w-16 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
      </React.Fragment>
    ))}
    <span className="sr-only">Loading performance data</span>
  </div>
);

// --- Main Dashboard Component ---

const GPHierarchyDashboard: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const { hierarchy, loading, error } = useSelector(
    (state: RootState) => state.gpHierarchy,
  );

  const today = format(new Date(), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [selectedUserIds, setSelectedUserIds] = useState<string[] | null>(null);
  const [isPartnerDealerModalOpen, setIsPartnerDealerModalOpen] =
    useState(false);
  const [fullHierarchy, setFullHierarchy] = useState<Node[]>([]);
  const selectionHierarchy = fullHierarchy.length ? fullHierarchy : hierarchy;
  const displayHierarchy = useMemo(
    () =>
      fullHierarchy.length
        ? mergeHierarchyMetrics(fullHierarchy, hierarchy)
        : hierarchy,
    [fullHierarchy, hierarchy],
  );
  const { visibleHierarchy, specialNodes } = useMemo(
    () => partitionPartnerAndDealerNodes(displayHierarchy),
    [displayHierarchy],
  );
  const channelPartners = specialNodes.filter(
    ({ node }) => node.role.trim().toLowerCase() === "channel partner",
  );
  const commissionDealers = specialNodes.filter(
    ({ node }) => node.role.trim().toLowerCase() === "commission dealer",
  );
  const filterPeople = useMemo(() => {
    const peopleById = new Map<string, HierarchyPerson>();
    specialNodes.forEach(({ hierarchyPath }) => {
      hierarchyPath.forEach((person) => peopleById.set(person._id, person));
    });
    return Array.from(peopleById.values()).sort((a, b) =>
      a.username.localeCompare(b.username),
    );
  }, [specialNodes]);

  useEffect(() => {
    if (selectedUserIds === null && hierarchy.length > 0) {
      setFullHierarchy(hierarchy);
    }
  }, [hierarchy, selectedUserIds]);

  const allUserIds = useMemo(
    () => getAllNodes(selectionHierarchy).map((node) => node._id),
    [selectionHierarchy],
  );
  const selectionRef = useRef<string[] | null>(selectedUserIds);
  const allUserIdsRef = useRef<string[]>(allUserIds);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const mobileSelectAllRef = useRef<HTMLInputElement>(null);
  selectionRef.current = selectedUserIds;
  allUserIdsRef.current = allUserIds;

  const selectedCount =
    selectedUserIds === null
      ? allUserIds.length
      : allUserIds.filter((id) => selectedUserIds.includes(id)).length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedCount > 0 && selectedCount < allUserIds.length;
    }
    if (mobileSelectAllRef.current) {
      mobileSelectAllRef.current.indeterminate =
        selectedCount > 0 && selectedCount < allUserIds.length;
    }
  }, [selectedCount, allUserIds.length]);

  useEffect(() => {
    const fromDate = from + "T00:00:00.000Z";
    const toDate = to + "T23:59:59.999Z";
    const currentSelection = selectionRef.current;
    const knownUserIds = allUserIdsRef.current;

    dispatch(
      fetchGPBasedOnOrders({
        fromDate,
        toDate,
        ...(currentSelection !== null && knownUserIds.length > 0
          ? {
              selectedUserIds: currentSelection,
              deselectedUserIds: knownUserIds.filter(
                (id) => !currentSelection.includes(id),
              ),
            }
          : {}),
      }),
    );
  }, [dispatch, from, to]);

  const handleSelectionChange = (nodeId: string, isSelected: boolean) => {
    const currentSelection = selectedUserIds ?? allUserIds;
    const node = getAllNodes(selectionHierarchy).find(
      (candidate) => candidate._id === nodeId,
    );
    const subtreeIds = node
      ? getAllNodes([node]).map((descendant) => descendant._id)
      : [nodeId];
    const nextSelection = isSelected
      ? Array.from(new Set([...currentSelection, ...subtreeIds]))
      : currentSelection.filter((id) => !subtreeIds.includes(id));

    setSelectedUserIds(nextSelection);
    selectionRef.current = nextSelection;
    dispatch(
      fetchGPBasedOnOrders({
        fromDate: `${from}T00:00:00.000Z`,
        toDate: `${to}T23:59:59.999Z`,
        selectedUserIds: nextSelection,
        deselectedUserIds: allUserIds.filter(
          (id) => !nextSelection.includes(id),
        ),
      }),
    );
  };

  const handleSelectAll = (isSelected: boolean) => {
    const nextSelection = isSelected ? allUserIds : [];
    setSelectedUserIds(nextSelection);
    selectionRef.current = nextSelection;
    dispatch(
      fetchGPBasedOnOrders({
        fromDate: `${from}T00:00:00.000Z`,
        toDate: `${to}T23:59:59.999Z`,
        selectedUserIds: nextSelection,
        deselectedUserIds: isSelected ? [] : allUserIds,
      }),
    );
  };

  const handlePresetSelect = (fromDate: string, toDate: string) => {
    setFrom(fromDate);
    setTo(toDate);
  };

  const summaryData = useMemo(() => {
    if (!hierarchy.length && !fullHierarchy.length) return null;

    const allNodes = getAllNodes(displayHierarchy);

    return {
      totalGP: hierarchy.reduce((sum, node) => sum + node.gp, 0),
      totalSales: hierarchy.reduce((sum, node) => sum + node.orderValue, 0),
      totalOrders: hierarchy.reduce((sum, node) => sum + node.orderCount, 0),
      teamMembers: allNodes.length - selectionHierarchy.length,
      activeUsers: allNodes.filter((node) => node.orderCount > 0).length,
    };
  }, [
    displayHierarchy,
    fullHierarchy.length,
    hierarchy,
    selectionHierarchy.length,
  ]);

  return (
    <div className="min-h-screen bg-[#f3f6f4] px-4 py-6 text-gray-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-screen-2xl">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-5 border-b border-gray-200 pb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-800 text-white">
              <BarChart3 size={22} strokeWidth={1.8} />
            </div>
            <div>
              <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                Sales analytics
              </p>
              <h1 className="text-2xl font-semibold leading-tight text-gray-950">
                GP hierarchy
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-700">
            <Users size={16} className="text-emerald-800" />
            <span className="font-semibold text-gray-950">{selectedCount}</span>
            <span>of {allUserIds.length} selected</span>
          </div>
        </header>

        {summaryData && (
          <section
            aria-label="Selected team performance totals"
            className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7"
          >
            <SummaryCard
              title="Total GP"
              value={formatGp(summaryData.totalGP)}
              subtitle={`${selectedCount} selected team members`}
              detail={
                summaryData.totalSales > 0
                  ? `${((summaryData.totalGP / summaryData.totalSales) * 100).toFixed(1)}% GP margin`
                  : "GP margin unavailable"
              }
              variant="gp"
            />
            <SummaryCard
              title="Total Sales"
              value={formatGp(summaryData.totalSales)}
              subtitle="Across selected teams"
              variant="sales"
            />
            <SummaryCard
              title="Total Orders"
              value={summaryData.totalOrders.toString()}
              subtitle="In date range"
            />
            <SummaryCard
              title="Team Members"
              value={summaryData.teamMembers.toString()}
              subtitle="In hierarchy"
            />
            <SummaryCard
              title="Active Users"
              value={summaryData.activeUsers.toString()}
              subtitle="In date range"
            />
          </section>
        )}

        <section className="mb-5 flex flex-col gap-4 border-y border-gray-200 bg-white px-4 py-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
                <CalendarDays size={14} /> Date range
              </label>
              <div className="flex flex-wrap gap-2">
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  aria-label="Start date"
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                />
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  aria-label="End date"
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                />
              </div>
            </div>
            <div className="pb-0.5">
              <DatePresets onPresetSelect={handlePresetSelect} />
            </div>
          </div>
          <p className="text-xs text-gray-500">All values shown in INR</p>
        </section>

        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Hierarchy users
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Partner and dealer accounts are shown separately.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsPartnerDealerModalOpen(true)}
              className="inline-flex min-h-10 items-center gap-2 rounded-md border border-emerald-800 bg-emerald-800 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2"
            >
              <Users size={16} aria-hidden="true" />
              Partners &amp; dealers
              <span className="rounded bg-white/15 px-1.5 py-0.5 text-xs tabular-nums">
                {specialNodes.length}
              </span>
            </button>
          </div>
          <div className="overflow-x-auto">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-[#eef3f0] px-3 py-3 text-sm text-gray-700 md:hidden">
              <label className="flex min-w-0 items-center gap-2 font-medium">
                <input
                  ref={mobileSelectAllRef}
                  type="checkbox"
                  checked={
                    allUserIds.length > 0 && selectedCount === allUserIds.length
                  }
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  aria-label="Select all users"
                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
                />
                <span>Select all users</span>
              </label>
              <span className="shrink-0 text-xs text-gray-500">
                {selectedCount}/{allUserIds.length} selected
              </span>
            </div>
            <div
              className={`hidden ${gridClasses} sticky top-0 z-10 items-center border-b border-gray-200 bg-[#eef3f0] text-[11px] font-semibold uppercase tracking-wide text-gray-600 md:grid`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={
                    allUserIds.length > 0 && selectedCount === allUserIds.length
                  }
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  aria-label="Select all users"
                  className="h-4 w-4 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
                />
                <span>Team member</span>
              </div>
              <div className="px-3 py-3 text-right">Sales</div>
              <div className="px-3 py-3 text-right">Own GP</div>
              <div className="px-3 py-3 text-right">Team GP</div>
              <div className="px-3 py-3 text-right">Total GP</div>
              <div className="px-3 py-3 text-center">Orders</div>
              <div className="px-4 py-3">Order numbers</div>
            </div>

            <div className="max-h-[800px] overflow-y-auto" aria-busy={loading}>
              {loading && displayHierarchy.length > 0 && (
                <div
                  role="status"
                  className="flex items-center gap-2 border-b border-emerald-100 bg-emerald-50/70 px-4 py-2 text-xs font-medium text-emerald-900"
                >
                  <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-700" />
                  Updating performance data
                </div>
              )}
              {error && displayHierarchy.length > 0 && (
                <div
                  role="alert"
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900"
                >
                  <span>
                    Refresh failed: {error}. Showing the last loaded data.
                  </span>
                  <button
                    onClick={() =>
                      dispatch(
                        fetchGPBasedOnOrders({
                          fromDate: `${from}T00:00:00.000Z`,
                          toDate: `${to}T23:59:59.999Z`,
                          ...(selectedUserIds !== null
                            ? {
                                selectedUserIds,
                                deselectedUserIds: allUserIds.filter(
                                  (id) => !selectedUserIds.includes(id),
                                ),
                              }
                            : {}),
                        }),
                      )
                    }
                    className="font-semibold underline underline-offset-2"
                  >
                    Retry
                  </button>
                </div>
              )}
              {loading && displayHierarchy.length === 0 ? (
                <HierarchyLoadingSkeleton />
              ) : error && displayHierarchy.length === 0 ? (
                <div className="m-4 border border-red-200 bg-red-50 p-6 text-center">
                  <CircleAlert
                    size={24}
                    className="mx-auto mb-3 text-red-700"
                  />
                  <p className="text-base font-semibold text-red-900">
                    Unable to Load Data
                  </p>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                  <button
                    onClick={() =>
                      dispatch(
                        fetchGPBasedOnOrders({
                          fromDate: `${from}T00:00:00.000Z`,
                          toDate: `${to}T23:59:59.999Z`,
                          ...(selectedUserIds !== null
                            ? {
                                selectedUserIds,
                                deselectedUserIds: allUserIds.filter(
                                  (id) => !selectedUserIds.includes(id),
                                ),
                              }
                            : {}),
                        }),
                      )
                    }
                    className="mt-4 rounded-md bg-red-800 px-4 py-2 text-sm font-medium text-white hover:bg-red-900"
                  >
                    Retry
                  </button>
                </div>
              ) : visibleHierarchy.length === 0 ? (
                <div className="p-14 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-50 text-emerald-800">
                    <Users size={23} />
                  </div>
                  <p className="mb-1 text-lg font-semibold text-gray-900">
                    No other hierarchy users
                  </p>
                  <p className="mx-auto max-w-md text-sm text-gray-500">
                    Channel Partners and Commission Dealers are available from
                    the Partners &amp; dealers list above.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 animate-fadeIn">
                  {visibleHierarchy.map((admin: Node) => (
                    <ConnectedNodeRow
                      key={admin._id}
                      node={admin}
                      level={0}
                      selectedUserIds={selectedUserIds}
                      onSelectionChange={handleSelectionChange}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      {isPartnerDealerModalOpen && (
        <PartnerDealerModal
          channelPartners={channelPartners}
          commissionDealers={commissionDealers}
          filterPeople={filterPeople}
          selectedUserIds={selectedUserIds}
          selectedCount={selectedCount}
          totalUsers={allUserIds.length}
          onSelectionChange={handleSelectionChange}
          onClose={() => setIsPartnerDealerModalOpen(false)}
        />
      )}
    </div>
  );
};

export default GPHierarchyDashboard;
